/**
 * Campaign links: `/:id/:channel?text=hello` auto-sends "hello" as the
 * learner's first message once they reach the chat, triggering the matching
 * RapidPro flow without any typing.
 *
 * The pending value lives in a per-channel localStorage slot rather than being
 * threaded through navigation, because the magic-link login path opens a new
 * tab — query params and in-tab state are gone there, localStorage is not.
 * The slot is consume-once and the param is stripped from the address bar on
 * capture, so a refresh never re-sends; opening the full campaign link again
 * deliberately re-triggers.
 */

export const PENDING_TEXT_PARAM = 'text';
const MAX_LENGTH = 200;

function slotKey(channelId, channelName) {
  return `webchat_pending_text:${channelId}:${channelName}`;
}

/** Trim, drop control characters, cap length. Returns '' when unusable. */
export function sanitizePendingText(raw) {
  if (typeof raw !== 'string') return '';
  return raw
    // eslint-disable-next-line no-control-regex -- strip C0/C1 controls from URL input
    .replace(/[\u0000-\u001f\u007f-\u009f]/g, '')
    .trim()
    .slice(0, MAX_LENGTH);
}

/**
 * Read `?text=` from a search string, stash it for this channel, and strip the
 * parameter from the address bar. Call on Login and Chat mount.
 */
export function capturePendingText(channelId, channelName, search) {
  if (!channelId || !channelName) return;
  let params;
  try {
    params = new URLSearchParams(search ?? window.location.search);
  } catch {
    return;
  }
  if (!params.has(PENDING_TEXT_PARAM)) return;

  const text = sanitizePendingText(params.get(PENDING_TEXT_PARAM));
  params.delete(PENDING_TEXT_PARAM);

  try {
    if (text) {
      window.localStorage.setItem(slotKey(channelId, channelName), text);
    }
  } catch {
    /* storage unavailable (private mode) — the flow just won't auto-start */
  }

  // Strip the param so refreshing the page doesn't re-trigger.
  if (typeof window !== 'undefined' && window.history?.replaceState) {
    const rest = params.toString();
    const next =
      window.location.pathname + (rest ? `?${rest}` : '') + window.location.hash;
    window.history.replaceState(null, '', next);
  }
}

/** Read and clear the pending text for this channel. Returns '' when none. */
export function consumePendingText(channelId, channelName) {
  if (!channelId || !channelName) return '';
  try {
    const key = slotKey(channelId, channelName);
    const value = window.localStorage.getItem(key) || '';
    window.localStorage.removeItem(key);
    return sanitizePendingText(value);
  } catch {
    return '';
  }
}
